import { create } from 'zustand';
import supabase from "@/lib/supabase";
import useAuthStore from "@/store/useAuthStore";
import usePetStore from "@/store/usePetStore";
import { PetAssets } from "@/constant/schema";
import { getLocalMonthString } from "@/lib/utils";

export interface Streak {
  cycle_days: number;    // 一个周期总共多少天
  gifts: any[];
  remainingDays: number; // 剩余多少天
  completeDays: number; // 已完成的打卡天数
  todayCompleted: boolean;        // 今天是否完成
  claimed: boolean[]; // 领取状态数组
  claimed_gifts: any[]; // 已领取的礼物数据数组
}

// 宝箱奖励类型定义
export interface ChestReward {
  type: 'diamond' | 'accessory' | 'card';
  value: number;
  itemId?: string;
  accessory?: any;
  card?: any;
}

interface StreakStore {
  streak: Streak | null;
  loading: boolean;
  initialized: boolean;
  isCreating: boolean;
  monthlyEventPopup: boolean;

  // Actions
  setStreak: (streak: Streak | null) => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
  fetchStreakData: () => Promise<void>;
  completeToday: () => Promise<boolean>;
  claimReward: (dayIndex: number, claimedGift?: any) => Promise<void>;
  generateCardReward: (cardType: 'small' | 'big') => Promise<ChestReward | null>;
  getAccessoryById: (id: string) => Promise<PetAssets | null>;
  getMinipetById: (id: number) => Promise<PetAssets | null>;
  getCardById: (id: number) => Promise<any | null>;
  saveAccessoryReward: (accessory: PetAssets, userId: string) => Promise<void>;
  addToMyCards: (cardId: number, userId: string) => Promise<void>;

  // Debug methods (only for learntest environment)
  debugCompleteNext: () => Promise<void>;
  debugResetAll: () => Promise<void>;
}

// Helper functions
function getTodayIndexInMonth(): number {
  return new Date().getDate() - 1;
}


function createMonthStartAt(month: string) {
  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    throw new Error(`Invalid month format: ${month}`);
  }

  // Use local timezone month start and persist as UTC timestamp.
  const startAtDate = new Date(year, monthIndex, 1, 0, 0, 0);
  const utcStartAt = startAtDate.toISOString().slice(0, 19) + 'Z';
  return utcStartAt;
}

async function getEventByMonth(month: string) {
  const user = useAuthStore.getState().user;
  if (!user) {
    throw new Error('User not found');
  }

  const { data, error } = await supabase
    .from('user_streak')
    .select('*')
    .eq('user_id', user.id)
    .eq('month', month)
    .order('start_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(`Error getting event for month ${month}: ${error.message}`);
  }
  return data;
}

async function getMonthlyEventConfig(month: string) {
  console.log('🔍 Fetching monthly event config for month:', month);
  const { data: currentEvent, error: currentEventError } = await supabase
    .from('monthly_event')
    .select('*')
    .eq('month', month)
    .single();

  console.log('🔍 Query result - data:', currentEvent, 'error:', currentEventError);

  if (currentEventError && currentEventError.code != "PGRST116") {
    console.error('❌ Database error:', currentEventError);
    throw new Error(`Error getting monthly event: ${currentEventError.message}`);
  }
  if (currentEventError || !currentEvent) {
    console.error('❌ No event found for month:', month);
    throw new Error(`No event configured for month: ${month}`);
  }
  console.log('✅ Event config found:', currentEvent);
  return currentEvent;
}

async function getAvailableAccessories(priceMin: number, priceMax: number): Promise<PetAssets[]> {
  const { data, error } = await supabase
    .from('pet_assets')
    .select('*')
    .eq('lock', false)
    .eq('type', 'normal')
    .gt('price', priceMin)
    .lte('price', priceMax);

  if (error) {
    console.error('Error fetching accessories:', error);
    return [];
  }

  return data || [];
}

async function getAccessoryById(id: string): Promise<PetAssets | null> {
  const { data, error } = await supabase
    .from('pet_assets')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching accessory by id:', error);
    return null;
  }

  return data;
}

async function getMinipetById(id: number): Promise<PetAssets | null> {
  const { data, error } = await supabase
    .from('pet_assets')
    .select('*')
    .eq('id', id)
    .eq('part', 'minipet')
    .single();

  if (error) {
    console.error('Error fetching minipet by id:', error);
    return null;
  }

  return data;
}

async function getCardById(id: number): Promise<any | null> {
  const { data, error } = await supabase
    .from('pet_cards')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching card by id:', error);
    return null;
  }

  return data;
}

async function getAvailableCards(series: string[]) {
  const { data, error } = await supabase
    .from('pet_cards')
    .select('id, image, level, series')
    .eq('lock', false)
    .in('series', series);

  if (error) {
    console.error('Error fetching cards:', error);
    return [];
  }

  return data || [];
}

// 生成卡片奖励的helper函数
async function generateCardRewardHelper(series: string[], probabilities: { R: number; SR: number; SSR?: number }): Promise<ChestReward | null> {
  const cards = await getAvailableCards(series);
  if (cards.length === 0) return null;

  const rCards = cards.filter(card => card.level === 'R');
  const srCards = cards.filter(card => card.level === 'SR');
  const ssrCards = cards.filter(card => card.level === 'SSR');

  let selectedCard;
  const cardRandom = Math.random();

  if (cardRandom < probabilities.R && rCards.length > 0) {
    selectedCard = rCards[Math.floor(Math.random() * rCards.length)];
  } else if (cardRandom < probabilities.R + probabilities.SR && srCards.length > 0) {
    selectedCard = srCards[Math.floor(Math.random() * srCards.length)];
  } else if (probabilities.SSR && cardRandom < probabilities.R + probabilities.SR + probabilities.SSR && ssrCards.length > 0) {
    selectedCard = ssrCards[Math.floor(Math.random() * ssrCards.length)];
  } else if (srCards.length > 0) {
    selectedCard = srCards[Math.floor(Math.random() * srCards.length)];
  } else if (rCards.length > 0) {
    selectedCard = rCards[Math.floor(Math.random() * rCards.length)];
  }

  if (selectedCard) {
    return {
      type: 'card',
      value: 0,
      itemId: selectedCard.id.toString(),
      card: selectedCard
    };
  }
  return null;
}

async function getAvailableMinipets() {
  const { data, error } = await supabase
    .from('pet_assets')
    .select('id, name, price, shop_thumbnail, part')
    .eq('lock', false)
    .eq('part', 'minipet');

  if (error) {
    console.error('Error fetching minipet accessories:', error);
    return [];
  }

  const userMinipets = usePetStore.getState().hasMinipets;
  return data?.filter(asset => !userMinipets.includes(asset.id)) || [];
}

function minipetClaimed(data: any) {
  return data.gifts.some((gift: any, index: number) => {
    return gift.minipet && data.claimed[index];
  });
}

// Create Zustand store with auto-initialization
export const useStreakStore = create<StreakStore>((set, get) => ({
  streak: null,
  loading: false,
  initialized: false,
  isCreating: false,
  monthlyEventPopup: false,

  setStreak: (streak) => set({ streak }),
  setLoading: (loading) => set({ loading }),

  initialize: async () => {
    const { initialized, loading } = get();

    // 防止重复初始化
    if (initialized || loading) {
      console.log('⚠️ Streak already initialized or loading, skipping...');
      return;
    }

    const user = useAuthStore.getState().user;

    if (!user) {
      console.log('⚠️ User not ready, skipping initialization');
      set({ streak: null, loading: false });
      return;
    }

    console.log('🚀 Initializing streak...');
    set({ loading: true, initialized: true });

    try {
      await get().fetchStreakData();
    } catch (error) {
      console.error('❌ Streak initialization failed:', error);
      set({ streak: null, initialized: false });
    } finally {
      set({ loading: false });
    }
  },

  fetchStreakData: async () => {

    try {
      console.log('📅 Starting monthly event fetch...');
      const month = getLocalMonthString();
      console.log('📅 Current month:', month);

      const eventConfig = await getMonthlyEventConfig(month);
      console.log('📅 Event config loaded:', eventConfig);

      const existingEvent = await getEventByMonth(month);
      console.log('📅 Existing event for month:', existingEvent);

      let event;
      if (!existingEvent) {
        console.log('📅 Creating new monthly event...');
        event = await createMonthlyEvent(month, eventConfig);
        console.log('📅 New event created:', event);
      } else {
        console.log('📅 Using existing month event');
        event = existingEvent;
      }

      const streak = transformStreakData(event);
      streak.gifts = eventConfig.gifts;
      console.log('📅 Setting streak:', streak);
      set({ streak });
    } catch (error) {
      console.error('❌ Monthly event fetch failed:', error);
      set({ streak: null });
      throw error;
    }
  },

  completeToday: async (): Promise<boolean> => {
    console.log("call completeToday");
    const user = useAuthStore.getState().user;
    if (!user) return false;

    try {
      const month = getLocalMonthString();
      const data = await getEventByMonth(month);
      if (!data || !data.start_at) {
        console.error(`get event failed for month: ${month}`);
        return false;
      }

      const todayIndex = getTodayIndexInMonth()
      const completes = [...(data.completes || [])];

      if (completes[todayIndex]) {
        console.log("Today already completed");
        return false;
      }

      completes[todayIndex] = true;

      const { error } = await supabase
        .from('user_streak')
        .update({ completes })
        .eq('id', data.id);

      if (error) throw error;

      await get().fetchStreakData();
      return true;
    } catch (error) {
      console.error('Error completing streak:', error);
      return false;
    }
  },

  claimReward: async (dayIndex: number, claimedGift?: any) => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    try {
      const month = getLocalMonthString();
      const data = await getEventByMonth(month);
      if (!data) return;

      const claimed = [...(data.claimed || [])];
      claimed[dayIndex] = true;

      // 更新 claimed_gifts 数组
      const claimedGifts = [...(data.claimed_gifts || [])];
      if (claimedGift) {
        claimedGifts[dayIndex] = claimedGift;
      }

      const { error } = await supabase
        .from('user_streak')
        .update({ claimed, claimed_gifts: claimedGifts })
        .eq('id', data.id);

      if (error) throw error;

      await get().fetchStreakData();
    } catch (error) {
      console.error('Error claiming reward:', error);
    }
  },

  generateCardReward: async (cardType: 'small' | 'big'): Promise<ChestReward | null> => {
    const chestCardSeries = ['Art Series', 'Comic Series'];
    const probabilities = cardType === 'small'
      ? { R: 0.8, SR: 0.2 }
      : { R: 0.1, SR: 0.8, SSR: 0.1 };

    return await generateCardRewardHelper(chestCardSeries, probabilities);
  },

  getAccessoryById: async (id: string): Promise<PetAssets | null> => {
    return await getAccessoryById(id);
  },

  getMinipetById: async (id: number): Promise<PetAssets | null> => {
    return await getMinipetById(id);
  },

  getCardById: async (id: number): Promise<any | null> => {
    return await getCardById(id);
  },

  saveAccessoryReward: async (accessory: PetAssets, userId: string) => {
    const addToCabinet = usePetStore.getState().addToCabinet;
    await addToCabinet({ [accessory.part]: accessory }, userId);
  },

  addToMyCards: async (cardId: number, userId: string) => {
    try {
      // 先获取当前的 my_cards 数据
      const { data: profileData, error: fetchError } = await supabase
        .from('profiles')
        .select('my_cards')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching my_cards:', fetchError);
        return;
      }

      // 获取当前 my_cards 或初始化为空数组
      const currentCards = profileData?.my_cards || [];

      // 改为可重复添加卡片20251024
      const newCards = [...currentCards, cardId];

      console.log('Adding to my_cards:', { cardId, newCards });

      // 更新 my_cards 数据
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ my_cards: newCards })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating my_cards:', updateError);
      } else {
        console.log('Successfully added card to my_cards');
      }
    } catch (error) {
      console.error('Error in addToMyCards:', error);
    }
  },

  // Debug method: Complete next day (only for learntest environment)
  debugCompleteNext: async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('User not found');
    }

    const month = getLocalMonthString();
    const streakData = await getEventByMonth(month);
    if (!streakData) {
      throw new Error('No active streak found');
    }

    // Find first false in completes array
    const completes = [...streakData.completes];
    const nextIndex = completes.findIndex((c: boolean) => c === false);

    if (nextIndex === -1) {
      throw new Error('All days already completed');
    }

    // Set to true
    completes[nextIndex] = true;

    // Update database
    const { error: updateError } = await supabase
      .from('user_streak')
      .update({ completes })
      .eq('id', streakData.id);

    if (updateError) {
      throw new Error(`Error updating streak: ${updateError.message}`);
    }

    // Refresh local state
    await get().fetchStreakData();
  },

  // Debug method: Reset all progress (only for learntest environment)
  debugResetAll: async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('User not found');
    }

    const month = getLocalMonthString();
    const streakData = await getEventByMonth(month);
    if (!streakData) {
      throw new Error('No active streak found');
    }

    // Reset all completes and claimed to false
    const cycleDays = streakData.cycle_days;
    const completes = new Array(cycleDays).fill(false);
    const claimed = new Array(cycleDays).fill(false);

    // Update database
    const { error: updateError } = await supabase
      .from('user_streak')
      .update({ completes, claimed })
      .eq('id', streakData.id);

    if (updateError) {
      throw new Error(`Error resetting streak: ${updateError.message}`);
    }

    // Refresh local state
    await get().fetchStreakData();
  },
}));

// Transform functions
function transformStreakData(data: any): Streak {
  const todayIndex = getTodayIndexInMonth();
  const completes = data.completes || [];
  const isTodayCompleted = completes[todayIndex] || false;

  return {
    cycle_days: data.cycle_days,
    remainingDays: data.cycle_days - todayIndex - 1,
    completeDays: completes.filter(Boolean).length,
    gifts: data.gifts,
    todayCompleted: isTodayCompleted,
    claimed: [...(data.claimed)],
    claimed_gifts: data.claimed_gifts || [],
  };
}

async function createMonthlyEvent(month: string, eventConfig: any) {
  const gifts = eventConfig.gifts;
  const cycleDays = gifts.length;
  const user = useAuthStore.getState().user;

  if (!user) {
    throw new Error('User not found');
  }

  const { data, error } = await supabase
    .from('user_streak')
    .insert({
      user_id: user.id,
      start_at: createMonthStartAt(month),
      cycle_days: cycleDays,
      completes: new Array(cycleDays).fill(false),
      claimed: new Array(cycleDays).fill(false),
      claimed_gifts: new Array(cycleDays).fill(null),
      month: month
    })
    .select()
    .single();

  if (error) {
    // Another request may have inserted the same month row concurrently.
    if (error.code === '23505' || error.message.includes('duplicate key')) {
      const existing = await getEventByMonth(month);
      if (existing) {
        return existing;
      }
    }
    throw new Error(`Error creating new event: ${error.message}`);
  }

  return data;
}
